import {useEffect, useRef, useState} from 'react';
import {Card, CardContent} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import {COLORS, EXPORTING} from '../../../utils/constants';
import {
    ACTUAL_AGE_GROUP_KEYS,
    AGE_GROUP_KEYS,
    latestPerUoa,
    meanOf,
    percentageOf,
    sumAnswers
} from '../../../utils/wia';
import {roundTo} from '../../../utils/round';

interface ITassoData {
    submissions: any[];
    actualSubmissions: any[];
    uoa: string | number
}

const Tasso = ({submissions, actualSubmissions, uoa}: ITassoData) => {
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
    const [options, setOptions] = useState<Highcharts.Options>();

    const calculateSubmissions = () => {
        let current = 0;
        let average = 0;

        const latest = latestPerUoa(submissions);
        const currentSubmission = latest.find((submission: any) => submission.uoa_id === uoa);

        if (currentSubmission) {
            const actualByUoa = new Map<number, any>(
                latestPerUoa(actualSubmissions).map((submission: any) => [submission.uoa_id, submission])
            );

            // The finalità is defined by question 3 of the scheda iniziale.
            const rates = latest
                .filter((submission: any) => submission.question4 === currentSubmission.question4)
                // An activity that previsioned nothing, or has not sent an aggiornamento yet, has no
                // rate at all: averaging it in as a zero would drag the benchmark down.
                .filter((submission: any) =>
                    actualByUoa.has(submission.uoa_id) && sumAnswers(submission, AGE_GROUP_KEYS) > 0)
                .map((submission: any) => ({
                    uoa_id: submission.uoa_id,
                    rate: percentageOf(
                        sumAnswers(actualByUoa.get(submission.uoa_id), ACTUAL_AGE_GROUP_KEYS),
                        sumAnswers(submission, AGE_GROUP_KEYS)
                    ),
                }));

            current = rates.find((rate: any) => rate.uoa_id === uoa)?.rate || 0;
            average = meanOf(rates.map((rate: any) => rate.rate));
        }

        const dashboardOptions: any = {
            chart: {
                height: 400,
                type: 'column',
                style: {
                    fontFamily: 'Rubik, sans-serif'
                },
                backgroundColor: '#fafafa'
            },
            title: {
                text: 'La mia attività e Media attività con stessa finalità in tutti i progetti',
                align: 'left'
            },
            xAxis: {
                categories: ['La mia attività', 'Media attività con stessa finalità in tutti i progetti']
            },
            yAxis: {
                min: 0,
                max: 100,
                labels: {
                    format: '{value}%'
                },
                title: {
                    text: ''
                }
            },
            tooltip: {
                valueSuffix: '%'
            },
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    stacking: 'normal',
                }
            },
            series: [
                {
                    showInLegend: false,
                    color: COLORS.blue,
                    name: '',
                    data: [
                        roundTo(current, 2),
                        roundTo(average, 2),
                    ]
                },
            ],
            exporting: EXPORTING
        };

        setOptions(dashboardOptions);
    };

    useEffect(() => {
        calculateSubmissions();
    }, []);

    return (
        <Card>
            <CardContent>
                {
                    options && (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={options}
                            ref={chartComponentRef}
                        />
                    )
                }
            </CardContent>
        </Card>
    );
};

export default Tasso;
